/**
 * ACTIVITY-CORE — textos humanos del feed (i18n).
 *
 * Parte ESPECÍFICA de la app (adaptar por clon): el mapa entity_type+action →
 * frase. La estructura (patch, agrupación, offline) vive en @famidex/shared.
 * Se devuelven descriptores `msg` (no strings) para que el idioma sea reactivo.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@famidex/shared";

interface ActivitySentence {
  /** Frase con `{name}` interpolado. */
  named: MessageDescriptor;
  /** Frase sin nombre (el patch no lo contiene). */
  anonymous: MessageDescriptor;
}

/**
 * Clave semántica fina: refina `updated`/`deleted` con lo que de verdad pasó
 * (completar, mover a papelera…) mirando el patch.
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "todo" && action === "updated" && patch.completed) {
    return patch.completed.new ? "completed" : "reopened";
  }
  if (action === "deleted") {
    // Transición de deleted_at = papelera; snapshot completo = purga definitiva.
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

const TODO_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added the task "{name}"`, anonymous: msg`added a task` },
  completed: { named: msg`completed the task "{name}"`, anonymous: msg`completed a task` },
  reopened: { named: msg`reopened the task "{name}"`, anonymous: msg`reopened a task` },
  updated: { named: msg`edited the task "{name}"`, anonymous: msg`edited a task` },
  trashed: {
    named: msg`moved the task "{name}" to the trash`,
    anonymous: msg`moved a task to the trash`,
  },
  purged: {
    named: msg`permanently deleted the task "{name}"`,
    anonymous: msg`permanently deleted a task`,
  },
  restored: { named: msg`restored the task "{name}"`, anonymous: msg`restored a task` },
  reverted: {
    named: msg`undid a change on the task "{name}"`,
    anonymous: msg`undid a change on a task`,
  },
};

const LIST_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the list "{name}"`, anonymous: msg`created a list` },
  updated: { named: msg`edited the list "{name}"`, anonymous: msg`edited a list` },
  trashed: {
    named: msg`moved the list "{name}" to the trash`,
    anonymous: msg`moved a list to the trash`,
  },
  purged: {
    named: msg`permanently deleted the list "{name}"`,
    anonymous: msg`permanently deleted a list`,
  },
  restored: { named: msg`restored the list "{name}"`, anonymous: msg`restored a list` },
  reverted: {
    named: msg`undid a change on the list "{name}"`,
    anonymous: msg`undid a change on a list`,
  },
};

// Las subtareas no tienen papelera: sus claves semánticas posibles son
// created / updated / purged (borrado duro) / reverted.
const SUBTASK_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added the subtask "{name}"`, anonymous: msg`added a subtask` },
  updated: { named: msg`edited the subtask "{name}"`, anonymous: msg`edited a subtask` },
  purged: { named: msg`deleted the subtask "{name}"`, anonymous: msg`deleted a subtask` },
  reverted: {
    named: msg`undid a change on the subtask "{name}"`,
    anonymous: msg`undid a change on a subtask`,
  },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

/** Nombre visible de la entidad extraído del patch (text/name, new u old). */
export function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["text", "name"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/**
 * Frase i18n de una entrada: descriptor + valores. Renderizar con
 * `t(descriptor, values)` (o i18n._) en el componente.
 */
export function activitySentence(row: ActivityRow): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const table =
    row.entity_type === "todo"
      ? TODO_TEXTS
      : row.entity_type === "list"
        ? LIST_TEXTS
        : row.entity_type === "subtask"
          ? SUBTASK_TEXTS
          : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** "hace 5 min" localizado con Intl; cae al día/fecha para lo antiguo. */
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < 7 * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
